import React, { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { buildUiTestTargetUrl, getUiTestScenario } from '@/modules/ui-test/scenarioRegistry'
import { UiTestRunBanner } from '@/modules/ui-test/UiTestRunBanner'
import { UiTestRunProvider } from '@/modules/ui-test/UiTestRunContext'
import { useI18n } from '@/contexts/I18nContext'
import { UI_TEST_ROUTE_PATH } from '@/modules/ui-test/constants'
import {
  advanceUiTestRunAll,
  finalizeUiTestRunAll,
  isUiTestRunAllActive,
  UI_TEST_RUN_QUERY,
  clearUiTestReturnPath,
  readUiTestReturnPath,
  UI_TEST_SHOW_RESULT_ALL_QUERY,
  UI_TEST_SHOW_RESULT_QUERY,
  writeUiTestResult,
  writeUiTestRunAllResult,
} from '@/modules/ui-test/uiTestSession'
import type { UiTestRunCompletePayload } from '@/modules/ui-test/uiTest.types'
import type { UiTestScenarioConfig } from '@/modules/ui-test/scenarioRegistry'
import { waitFor } from '@/modules/ui-test/utils/uiTestDomActions'
import { useUiTestRun } from '@/modules/ui-test/UiTestRunContext'

type UiTestLiveExecutorProps = {
  scenario: UiTestScenarioConfig
}

const UiTestLiveExecutor: React.FC<UiTestLiveExecutorProps> = ({ scenario }) => {
  const { t } = useI18n()
  const r = (k: string) => t(`components.menu.uiTestPage.${k}`)
  const uiTest = useUiTestRun()
  const navigate = uiTest.navigate
  const startedRef = useRef(false)

  const finish = useCallback(
    (payload: UiTestRunCompletePayload) => {
      const returnToCatalog = (queryKey: string) => {
        const returnPath = readUiTestReturnPath(UI_TEST_ROUTE_PATH)
        clearUiTestReturnPath()
        const qs = new URLSearchParams({ [queryKey]: '1' })
        navigate(`${returnPath}?${qs.toString()}`, { replace: true })
      }

      if (isUiTestRunAllActive()) {
        const next = advanceUiTestRunAll(payload)
        if (next) {
          navigate(buildUiTestTargetUrl(next), { replace: true })
          return
        }
        writeUiTestRunAllResult(finalizeUiTestRunAll())
        returnToCatalog(UI_TEST_SHOW_RESULT_ALL_QUERY)
        return
      }

      writeUiTestResult(payload)
      returnToCatalog(UI_TEST_SHOW_RESULT_QUERY)
    },
    [navigate],
  )

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const run = async () => {
      try {
        await waitFor(() => Boolean(uiTest.screenRootRef.current), 8000, 80)
        const summary = await scenario.run(uiTest)
        finish({
          summary,
          logs: uiTest.getLogs(),
          scenarioId: scenario.id,
        })
      } catch (e) {
        finish({
          summary: {
            ok: false,
            passed: 0,
            skipped: 0,
            failed: 0,
            total: 0,
            features: [],
            message: (e as { message?: string })?.message || r('failed'),
          },
          logs: uiTest.getLogs(),
          scenarioId: scenario.id,
          errorMessage: (e as { message?: string })?.message || r('failed'),
        })
      }
    }

    void run()
  }, [finish, r, scenario, uiTest])

  return <UiTestRunBanner />
}

type UiTestLiveRunnerProps = {
  screenRootRef: React.RefObject<HTMLDivElement | null>
}

/** Chạy kịch bản trên màn hình thật khi URL có `?uiTestRun=...`. */
const UiTestLiveRunner: React.FC<UiTestLiveRunnerProps> = ({ screenRootRef }) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const scenarioId = searchParams.get(UI_TEST_RUN_QUERY)
  const scenario = getUiTestScenario(scenarioId)

  if (!scenario) return null

  return (
    <UiTestRunProvider
      key={scenario.id}
      scenarioId={scenario.id}
      screenRootRef={screenRootRef}
      navigate={navigate}
    >
      <UiTestLiveExecutor scenario={scenario} />
    </UiTestRunProvider>
  )
}

export default UiTestLiveRunner
